import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";
import { invalidatePurchaseQueries, invalidateInventoryQueries } from "@/lib/mutationHelpers";

/** delivery_status: ordered = not yet received; delivered = received */
export type CleanerPurchaseDeliveryStatus = "ordered" | "delivered";

export interface CleanerPurchase {
  id: string;
  property_id: string;
  cleaner_id: string;
  item_id: string | null;
  quantity: number;
  amount: number;
  receipt_url: string | null;
  note: string | null;
  status: "pending_approval" | "auto_approved" | "approved" | "paid" | "rejected";
  type: "request_order" | "cleaner_purchase";
  created_at: string;
  paid_at: string | null;
  delivery_status: CleanerPurchaseDeliveryStatus;
  proof_url: string | null;
  delivered_at: string | null;
  vendor_source: string | null;
  currency: string;
  /** True after quantity has been applied to inventory (prevents double-apply). */
  inventory_applied: boolean;
}

export interface CleanerPurchaseWithItem extends CleanerPurchase {
  inventory_items: { item_name: string } | null;
}

/** Line item for multi-item purchase (cleaner_purchase_line_items + item name). */
export interface TurnoverPurchaseLineItem {
  inventory_item_id: string;
  quantity: number;
  item_name: string | null;
}

/** Purchase for "Purchases for this turnover" list (cleaner_purchases + item_name, room_id, line_items). */
export interface TurnoverPurchase extends CleanerPurchase {
  item_name: string | null;
  room_id: string | null;
  line_items?: TurnoverPurchaseLineItem[];
}

const TURNOVER_PURCHASES_QUERY_KEY = "turnover-purchases";

/** Resolve cleaner id from the signed-in user when callers pass an empty prop. */
async function resolveCleanerIdForPurchase(cleanerId: string): Promise<string> {
  const trimmed = cleanerId?.trim();
  if (trimmed) return trimmed;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in");

  const { data: cleaner, error } = await supabase
    .from("cleaners")
    .select("id")
    .eq("user_id", user.id)
    .maybeSingle();
  if (error) throw error;
  if (!cleaner?.id) {
    throw new Error("Cleaner profile not found. Ask the host to confirm your invite, then sign out and back in.");
  }
  return cleaner.id;
}

/** All purchases (and request-order entries) for a single cleaning task. Used in Inventory check "Purchases for this turnover". */
export const useTurnoverPurchases = (cleaningTaskId: string | undefined) => {
  return useQuery({
    queryKey: [TURNOVER_PURCHASES_QUERY_KEY, cleaningTaskId],
    queryFn: async (): Promise<TurnoverPurchase[]> => {
      if (!cleaningTaskId) return [];
      const { data: purchases, error } = await supabase
        .from("cleaner_purchases")
        .select("*")
        .eq("cleaning_task_id", cleaningTaskId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const list = (purchases ?? []) as CleanerPurchase[];
      if (list.length === 0) return [];

      const itemIds = [...new Set(list.map((p) => p.item_id).filter(Boolean))] as string[];
      let itemMap = new Map<string, { item_name: string | null; room_id: string | null }>();
      if (itemIds.length > 0) {
        const { data: items } = await supabase
          .from("inventory_items")
          .select("id, item_name, room_id")
          .in("id", itemIds);
        itemMap = new Map(
          (items ?? []).map((i) => [i.id, { item_name: i.item_name ?? null, room_id: i.room_id ?? null }])
        );
      }

      const multiIds = list.filter((p) => p.item_id == null).map((p) => p.id);
      let lineItemsByPurchase: Record<string, TurnoverPurchaseLineItem[]> = {};
      if (multiIds.length > 0) {
        const { data: lineRows } = await supabase
          .from("cleaner_purchase_line_items")
          .select("purchase_id, inventory_item_id, quantity")
          .in("purchase_id", multiIds);
        if (lineRows?.length) {
          const allItemIds = [...new Set(lineRows.map((r) => r.inventory_item_id))];
          const { data: lineItemNames } = await supabase
            .from("inventory_items")
            .select("id, item_name")
            .in("id", allItemIds);
          const nameMap = new Map((lineItemNames ?? []).map((i) => [i.id, i.item_name ?? null]));
          lineItemsByPurchase = lineRows.reduce((acc, r) => {
            const arr = acc[r.purchase_id] ?? [];
            arr.push({
              inventory_item_id: r.inventory_item_id,
              quantity: r.quantity,
              item_name: nameMap.get(r.inventory_item_id) ?? null,
            });
            acc[r.purchase_id] = arr;
            return acc;
          }, {} as Record<string, TurnoverPurchaseLineItem[]>);
        }
      }

      return list.map((p) => {
        const itemInfo = p.item_id ? itemMap.get(p.item_id) : null;
        return {
          ...p,
          item_name: itemInfo?.item_name ?? null,
          room_id: itemInfo?.room_id ?? null,
          line_items: lineItemsByPurchase[p.id] ?? undefined,
        };
      });
    },
    enabled: !!cleaningTaskId,
  });
};

export const useCleanerPurchases = (propertyId: string | undefined, cleanerId: string | undefined) => {
  return useQuery({
    queryKey: [...queryKeys.billing.purchases.byCleaner(propertyId || ''), cleanerId],
    queryFn: async () => {
      if (!propertyId || !cleanerId) return [];
      
      const { data, error } = await supabase
        .from("cleaner_purchases")
        .select("*")
        .eq("property_id", propertyId)
        .eq("cleaner_id", cleanerId)
        .order("created_at", { ascending: false });

      // Handle 400 as empty state (no purchases = empty array)
      if (error && error.code === '400') {
        return [];
      }
      if (error) throw error;
      return (data ?? []) as CleanerPurchase[];
    },
    enabled: !!propertyId && !!cleanerId,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

// Fetch all purchases for the current cleaner across all properties
export const useAllCleanerPurchases = (cleanerId: string | undefined) => {
  return useQuery({
    queryKey: [...queryKeys.billing.purchases.all, cleanerId],
    queryFn: async () => {
      if (!cleanerId) return [];
      
      const { data, error } = await supabase
        .from("cleaner_purchases")
        .select(`
          *,
          inventory_items(item_name)
        `)
        .eq("cleaner_id", cleanerId)
        .order("created_at", { ascending: false });

      // Handle 400 as empty state (no purchases = empty array)
      if (error && error.code === '400') {
        return [];
      }
      if (error) throw error;
      return (data ?? []) as CleanerPurchaseWithItem[];
    },
    enabled: !!cleanerId,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useCreateCleanerPurchase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      cleanerId,
      itemId,
      quantity,
      amount,
      receiptUrl,
      note,
      type,
      status,
      cleaningTaskId,
      scheduledDate,
      deliveryStatus,
      proofUrl,
      deliveredAt,
      vendorSource,
      currency,
      receiptTotalCents,
      receiptTotalConfidence,
      receiptTotalStatus,
      receiptTotalSource,
    }: {
      propertyId: string;
      cleanerId: string;
      itemId?: string;
      quantity: number;
      amount: number;
      receiptUrl?: string;
      note?: string;
      type: "request_order" | "cleaner_purchase";
      status: "pending_approval" | "auto_approved";
      cleaningTaskId?: string | null;
      /** When provided with cleaningTaskId, used to attach reimbursement to invoice period (e.g. task scheduled_date) */
      scheduledDate?: string | null;
      deliveryStatus?: CleanerPurchaseDeliveryStatus;
      proofUrl?: string | null;
      deliveredAt?: string | null;
      vendorSource?: string | null;
      currency?: string;
      receiptTotalCents?: number | null;
      receiptTotalConfidence?: number | null;
      receiptTotalStatus?: "matched" | "mismatch" | "unknown" | null;
      receiptTotalSource?: string | null;
    }) => {
      const resolvedCleanerId = await resolveCleanerIdForPurchase(cleanerId);

      let invoicePeriodId: string | null = null;
      if (propertyId && scheduledDate) {
        const { data: periodId, error: rpcError } = await supabase.rpc("get_or_create_invoice_period", {
          p_property_id: propertyId,
          p_date: scheduledDate,
        });
        if (!rpcError) invoicePeriodId = periodId;
      }

      const insertPayload: Record<string, unknown> = {
        property_id: propertyId,
        cleaner_id: resolvedCleanerId,
        item_id: itemId || null,
        quantity,
        amount,
        receipt_url: receiptUrl || null,
        note: note || null,
        type,
        status: "pending_approval",
      };
      if (cleaningTaskId) insertPayload.cleaning_task_id = cleaningTaskId;
      if (invoicePeriodId) insertPayload.invoice_period_id = invoicePeriodId;
      if (deliveryStatus != null) insertPayload.delivery_status = deliveryStatus;
      if (proofUrl != null) insertPayload.proof_url = proofUrl;
      if (deliveredAt != null) insertPayload.delivered_at = deliveredAt;
      if (vendorSource != null) insertPayload.vendor_source = vendorSource;
      if (currency != null) insertPayload.currency = currency;
      if (receiptTotalCents != null) insertPayload.receipt_total_cents = receiptTotalCents;
      if (receiptTotalConfidence != null) insertPayload.receipt_total_confidence = receiptTotalConfidence;
      if (receiptTotalStatus != null) insertPayload.receipt_total_status = receiptTotalStatus;
      if (receiptTotalSource != null) insertPayload.receipt_total_source = receiptTotalSource;

      const { data, error } = await supabase
        .from("cleaner_purchases")
        .insert(insertPayload)
        .select()
        .single();

      if (error) throw error;

      // Apply purchase quantity to inventory (in_stock_qty or ordered_qty by delivery_status)
      if (type === "cleaner_purchase" && data?.id && itemId) {
        const { error: rpcErr } = await supabase.rpc("apply_cleaner_purchase_to_inventory", {
          p_purchase_id: data.id,
        });
        if (rpcErr) console.error("[apply_cleaner_purchase_to_inventory]", rpcErr);
      }

      let chargeResult: { charged?: boolean; reason?: string } = {};
      if (type === "cleaner_purchase" && data?.id) {
        const { data: chargeData, error: chargeErr } = await supabase.functions.invoke("charge-cleaner-purchase", {
          body: { purchase_id: data.id },
        });
        if (chargeErr) {
          chargeResult = { charged: false, reason: chargeErr.message || "Charge request failed" };
        } else if (chargeData && typeof chargeData === "object") {
          chargeResult = {
            charged: !!(chargeData as { charged?: boolean }).charged,
            reason: (chargeData as { reason?: string }).reason,
          };
        }
      }

      return { purchase: data, chargeResult };
    },
    onSuccess: (_, variables) => {
      invalidatePurchaseQueries(queryClient, variables.propertyId);
      if (variables.itemId) invalidateInventoryQueries(queryClient, variables.propertyId);
      if (variables.cleaningTaskId) {
        queryClient.invalidateQueries({ queryKey: [TURNOVER_PURCHASES_QUERY_KEY, variables.cleaningTaskId] });
      }
    },
  });
};

/** Multi-item (one receipt): one purchase with line items. item_id null, quantity 0. */
export interface CleanerPurchaseLineItemInput {
  inventory_item_id: string;
  quantity: number;
}

export const useCreateCleanerPurchaseMulti = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      cleanerId,
      amount,
      receiptUrl,
      cleaningTaskId,
      scheduledDate,
      deliveryStatus,
      lineItems,
      receiptTotalCents,
      receiptTotalConfidence,
      receiptTotalStatus,
      receiptTotalSource,
    }: {
      propertyId: string;
      cleanerId: string;
      amount: number;
      receiptUrl: string;
      cleaningTaskId?: string | null;
      scheduledDate?: string | null;
      deliveryStatus: CleanerPurchaseDeliveryStatus;
      lineItems: CleanerPurchaseLineItemInput[];
      receiptTotalCents?: number | null;
      receiptTotalConfidence?: number | null;
      receiptTotalStatus?: "matched" | "mismatch" | "unknown" | null;
      receiptTotalSource?: string | null;
    }) => {
      if (!lineItems.length) throw new Error("At least one line item required");

      let invoicePeriodId: string | null = null;
      if (propertyId && scheduledDate) {
        const { data: periodId, error: rpcError } = await supabase.rpc("get_or_create_invoice_period", {
          p_property_id: propertyId,
          p_date: scheduledDate,
        });
        if (!rpcError) invoicePeriodId = periodId;
      }

      const insertPayload: Record<string, unknown> = {
        property_id: propertyId,
        cleaner_id: cleanerId,
        item_id: null,
        quantity: 0,
        amount,
        receipt_url: receiptUrl,
        note: null,
        type: "cleaner_purchase",
        status: "pending_approval",
        delivery_status: deliveryStatus,
        proof_url: null,
        delivered_at: deliveryStatus === "delivered" ? new Date().toISOString() : null,
        currency: "USD",
      };
      if (cleaningTaskId) insertPayload.cleaning_task_id = cleaningTaskId;
      if (invoicePeriodId) insertPayload.invoice_period_id = invoicePeriodId;
      if (receiptTotalCents != null) insertPayload.receipt_total_cents = receiptTotalCents;
      if (receiptTotalConfidence != null) insertPayload.receipt_total_confidence = receiptTotalConfidence;
      if (receiptTotalStatus != null) insertPayload.receipt_total_status = receiptTotalStatus;
      if (receiptTotalSource != null) insertPayload.receipt_total_source = receiptTotalSource;

      const { data: purchase, error: purchaseError } = await supabase
        .from("cleaner_purchases")
        .insert(insertPayload)
        .select()
        .single();

      if (purchaseError) throw purchaseError;
      if (!purchase?.id) throw new Error("Purchase insert failed");

      const { error: lineError } = await supabase
        .from("cleaner_purchase_line_items")
        .insert(
          lineItems.map((li) => ({
            purchase_id: purchase.id,
            inventory_item_id: li.inventory_item_id,
            quantity: Math.max(1, Math.floor(li.quantity)),
          }))
        );

      if (lineError) throw lineError;

      const { error: rpcErr } = await supabase.rpc("apply_cleaner_purchase_to_inventory", {
        p_purchase_id: purchase.id,
      });
      if (rpcErr) console.error("[apply_cleaner_purchase_to_inventory]", rpcErr);

      supabase.functions.invoke("charge-cleaner-purchase", {
        body: { purchase_id: purchase.id },
      }).then((r) => console.log("Charge result:", r.data)).catch((err) => console.error("Charge error:", err));

      return purchase;
    },
    onSuccess: (_, variables) => {
      invalidatePurchaseQueries(queryClient, variables.propertyId);
      invalidateInventoryQueries(queryClient, variables.propertyId);
      if (variables.cleaningTaskId) {
        queryClient.invalidateQueries({ queryKey: [TURNOVER_PURCHASES_QUERY_KEY, variables.cleaningTaskId] });
      }
    },
  });
};

/** Delete a purchase (draft). Removes line items first, then the purchase. Only use for pending entries before turnover submit. */
export const useDeleteCleanerPurchase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      purchaseId,
      propertyId,
      cleaningTaskId,
    }: {
      purchaseId: string;
      propertyId: string;
      cleaningTaskId?: string | null;
    }) => {
      await supabase.from("cleaner_purchase_line_items").delete().eq("purchase_id", purchaseId);
      const { error } = await supabase.from("cleaner_purchases").delete().eq("id", purchaseId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      invalidatePurchaseQueries(queryClient, variables.propertyId);
      if (variables.cleaningTaskId) {
        queryClient.invalidateQueries({ queryKey: [TURNOVER_PURCHASES_QUERY_KEY, variables.cleaningTaskId] });
      }
    },
  });
};

/** Update a cleaner purchase (amount, quantity, note). For "Edit" in Purchases for this turnover. */
export const useUpdateCleanerPurchase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      purchaseId,
      propertyId,
      cleaningTaskId,
      amount,
      quantity,
      note,
    }: {
      purchaseId: string;
      propertyId: string;
      cleaningTaskId?: string | null;
      amount?: number;
      quantity?: number;
      note?: string | null;
    }) => {
      const payload: Record<string, unknown> = {};
      if (amount !== undefined) payload.amount = amount;
      if (quantity !== undefined) payload.quantity = quantity;
      if (note !== undefined) payload.note = note ?? null;
      if (Object.keys(payload).length === 0) return null;
      const { data, error } = await supabase
        .from("cleaner_purchases")
        .update(payload)
        .eq("id", purchaseId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      invalidatePurchaseQueries(queryClient, variables.propertyId);
      if (variables.cleaningTaskId) {
        queryClient.invalidateQueries({ queryKey: [TURNOVER_PURCHASES_QUERY_KEY, variables.cleaningTaskId] });
      }
    },
  });
};
